import { AlignmentType, Document, IRunOptions, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';

import { cloneBiddingSummaryExportRoot } from 'components/molecules/bidding_summary_export_dom';

const DOCX_PAGE_MARGINS = {
  top: 1440,
  right: 1440,
  bottom: 1440,
  left: 1440,
};

const SPACING_AFTER_TEXT = { after: 200 };
const SPACING_AFTER_SECTION_TITLE = { before: 320, after: 160 };

const getTextRunsFromNode = (
  node: ChildNode,
  inheritedStyles: Partial<IRunOptions> = {},
): TextRun[] => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent?.replace(/\u00a0/g, ' ') ?? '';
    if (!text.trim()) return [];

    return [
      new TextRun({
        text,
        ...inheritedStyles,
      }),
    ];
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return [];

  const element = node as HTMLElement;
  const tagName = element.tagName.toLowerCase();
  const nextStyles = { ...inheritedStyles };

  if (tagName === 'strong' || tagName === 'b') nextStyles.bold = true;
  if (tagName === 'em' || tagName === 'i') nextStyles.italics = true;
  if (tagName === 'u') nextStyles.underline = {};

  if (tagName === 'br') {
    return [new TextRun({ text: '', break: 1, ...inheritedStyles })];
  }

  return Array.from(element.childNodes).flatMap((child) => getTextRunsFromNode(child, nextStyles));
};

const getTextRunsFromElement = (element: HTMLElement, baseStyles: Partial<IRunOptions> = {}) => {
  if (!element.childNodes.length) {
    const text = element.textContent?.trim();
    return text ? [new TextRun({ text, ...baseStyles })] : [];
  }

  return Array.from(element.childNodes).flatMap((child) => getTextRunsFromNode(child, baseStyles));
};

const paragraphFromText = (
  text: string,
  options: {
    bold?: boolean;
    size?: number;
    alignment?: typeof AlignmentType[keyof typeof AlignmentType];
    spacing?: { before?: number; after?: number };
  } = {},
): Paragraph =>
  new Paragraph({
    children: [
      new TextRun({
        text,
        bold: options.bold,
        size: options.size,
      }),
    ],
    alignment: options.alignment,
    spacing: options.spacing ?? SPACING_AFTER_TEXT,
  });

const getParagraphsFromList = (listElement: HTMLElement): Paragraph[] =>
  Array.from(listElement.querySelectorAll(':scope > li')).map(
    (item) =>
      new Paragraph({
        children: [new TextRun({ text: '• ' }), ...getTextRunsFromElement(item as HTMLElement)],
        spacing: { after: 120 },
        indent: { left: 360 },
      }),
  );

const getParagraphsFromElement = (element: HTMLElement): Paragraph[] => {
  const { classList } = element;
  const tagName = element.tagName.toLowerCase();

  if (
    classList.contains('bidding-summary-omit-share-empty') ||
    classList.contains('bidding-summary-omit-from-print-share') ||
    classList.contains('d-print-none')
  ) {
    return [];
  }

  if (classList.contains('summary-accordion-card')) {
    return [];
  }

  if (classList.contains('mb-3') && element.querySelector('.summary-field-label')) {
    const label = element.querySelector('.summary-field-label')?.textContent?.trim();
    const value = element.querySelector('.summary-field-value')?.textContent?.trim();

    if (!label) return [];

    return [
      new Paragraph({
        children: [
          new TextRun({ text: label, bold: true }),
          new TextRun({ text: value ? `: ${value}` : '' }),
        ],
        spacing: SPACING_AFTER_TEXT,
      }),
    ];
  }

  if (classList.contains('bidding-summary-garantia-card')) {
    const title = element.querySelector('.font-weight-bold')?.textContent?.trim();
    const description = element.querySelector('p')?.textContent?.trim();
    const paragraphs: Paragraph[] = [];

    if (title) {
      paragraphs.push(paragraphFromText(title, { bold: true, spacing: { after: 120 } }));
    }
    if (description) {
      paragraphs.push(paragraphFromText(description));
    }

    return paragraphs;
  }

  if (classList.contains('btn')) {
    return [];
  }

  if (classList.contains('summary-capag-content')) {
    const paragraphs: Paragraph[] = [];
    const badgeTitle = element.querySelector('.summary-capag-badge-title')?.textContent?.trim();
    const badgeValue = element.querySelector('.summary-capag-badge-value')?.textContent?.trim();

    if (badgeTitle && badgeValue) {
      paragraphs.push(
        paragraphFromText(`${badgeTitle}: ${badgeValue}`, { bold: true, spacing: { after: 160 } }),
      );
    }

    element.querySelectorAll('.summary-capag-indicator-row').forEach((row) => {
      const label = row.querySelector('.summary-capag-indicator-label')?.textContent?.trim();
      const valueElement = row.querySelector(':scope > div');
      const valueText =
        valueElement?.textContent
          ?.trim()
          .replace(/\s+/g, ' ')
          .replace(/\s*\|\s*/g, ' | ') ?? '—';

      if (label) {
        paragraphs.push(paragraphFromText(`${label}: ${valueText}`, { spacing: { after: 120 } }));
      }
    });

    return paragraphs;
  }

  if (classList.contains('bidder-consideration-team-card')) {
    const author = element
      .querySelector('.bidder-consideration-team-card__header .font-weight-bold')
      ?.textContent?.trim();
    const date = element.querySelector('time')?.textContent?.trim();
    const body = element
      .querySelector('.bidder-consideration-team-card__body')
      ?.textContent?.trim();
    const paragraphs: Paragraph[] = [];

    if (author) {
      const meta = date ? `${author} — ${date}` : author;
      paragraphs.push(paragraphFromText(meta, { bold: true, spacing: { after: 120 } }));
    }
    if (body) {
      paragraphs.push(paragraphFromText(body));
    }

    return paragraphs;
  }

  if (tagName === 'h3' || tagName === 'h4' || tagName === 'h5' || tagName === 'h6') {
    const text = element.textContent?.trim();
    return text ? [paragraphFromText(text, { bold: true, spacing: { after: 160 } })] : [];
  }

  if (tagName === 'ul') {
    return getParagraphsFromList(element);
  }

  if (tagName === 'p') {
    const text = element.textContent?.trim();
    if (!text) return [];

    return [
      new Paragraph({
        children: getTextRunsFromElement(element),
        spacing: SPACING_AFTER_TEXT,
      }),
    ];
  }

  if (tagName === 'textarea') {
    const text = (element as HTMLTextAreaElement).value.trim();
    return text ? [paragraphFromText(text)] : [];
  }

  if (element.children.length > 0) {
    const hasDirectTextNodes = Array.from(element.childNodes).some(
      (node) => node.nodeType === Node.TEXT_NODE && Boolean(node.textContent?.trim()),
    );

    if (hasDirectTextNodes) {
      const text = element.textContent?.trim().replace(/\s+/g, ' ');
      return text ? [paragraphFromText(text)] : [];
    }

    return Array.from(element.children).flatMap((child) =>
      getParagraphsFromElement(child as HTMLElement),
    );
  }

  const text = element.textContent?.trim();
  return text ? [paragraphFromText(text)] : [];
};

const getParagraphsFromAccordionBody = (body: HTMLElement): Paragraph[] =>
  Array.from(body.childNodes).flatMap((node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return [];
    const element = node as HTMLElement;
    if (element.classList.contains('summary-accordion-card')) return [];
    return getParagraphsFromElement(element);
  });

const getCoverParagraphs = (root: HTMLElement): Paragraph[] => {
  const paragraphs: Paragraph[] = [];
  const coverTitle = root.querySelector('.summary-print-cover-title')?.textContent?.trim();
  const coverMeta = root.querySelector('.summary-print-cover-meta')?.textContent?.trim();
  const coverDate = root.querySelector('.summary-print-cover-date')?.textContent?.trim();

  if (coverTitle) {
    paragraphs.push(
      new Paragraph({
        children: [new TextRun({ text: coverTitle, bold: true, size: 32 })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
      }),
    );
  }

  if (coverMeta) {
    paragraphs.push(
      paragraphFromText(coverMeta, { alignment: AlignmentType.CENTER, spacing: { after: 120 } }),
    );
  }

  if (coverDate) {
    paragraphs.push(
      paragraphFromText(coverDate, { alignment: AlignmentType.CENTER, spacing: { after: 320 } }),
    );
  }

  return paragraphs;
};

const getStatCardParagraphs = (root: HTMLElement): Paragraph[] =>
  Array.from(root.querySelectorAll('.summary-stat-card')).flatMap((card) => {
    const label = card.querySelector('.summary-stat-label')?.textContent?.trim();
    const value =
      card.querySelector('.h5')?.textContent?.trim() ||
      card.querySelector('.summary-field-value')?.textContent?.trim() ||
      card.querySelector('.text-dark')?.textContent?.trim();
    const badge = card.querySelector('.badge')?.textContent?.trim();

    if (!label) return [];

    const valueText = [value, badge].filter(Boolean).join(' — ') || '—';

    return [
      new Paragraph({
        children: [
          new TextRun({ text: `${label}: `, bold: true }),
          new TextRun({ text: valueText }),
        ],
        spacing: SPACING_AFTER_TEXT,
      }),
    ];
  });

const getAccordionParagraphs = (root: HTMLElement): Paragraph[] =>
  Array.from(root.querySelectorAll('.summary-accordion-card')).flatMap((card) => {
    const title = card.querySelector('.summary-accordion-title')?.textContent?.trim();
    const badge = card.querySelector('.summary-accordion-badge')?.textContent?.trim();
    const body = card.querySelector('.summary-accordion-body');

    const paragraphs: Paragraph[] = [];

    if (title) {
      const sectionTitle = badge ? `${title} — ${badge}` : title;
      paragraphs.push(
        new Paragraph({
          children: [new TextRun({ text: sectionTitle, bold: true, size: 26 })],
          spacing: SPACING_AFTER_SECTION_TITLE,
        }),
      );
    }

    if (body) {
      paragraphs.push(...getParagraphsFromAccordionBody(body as HTMLElement));
    }

    return paragraphs;
  });

const getDocxParagraphsFromSummaryRoot = (root: HTMLElement): Paragraph[] => {
  const paragraphs = [
    ...getCoverParagraphs(root),
    ...getStatCardParagraphs(root),
    ...getAccordionParagraphs(root),
  ];

  return paragraphs.length ? paragraphs : [paragraphFromText('Conteúdo indisponível.')];
};

export const buildBiddingSummaryDocxBlob = async (
  root: HTMLElement | null,
): Promise<Blob | null> => {
  if (!root) return null;

  const preparedRoot = cloneBiddingSummaryExportRoot(root, { expandSections: true });
  const paragraphs = getDocxParagraphsFromSummaryRoot(preparedRoot);

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: DOCX_PAGE_MARGINS,
          },
        },
        children: paragraphs,
      },
    ],
  });

  return Packer.toBlob(doc);
};

export const downloadBiddingSummaryDocx = async (
  root: HTMLElement | null,
  filename: string,
): Promise<boolean> => {
  const docBlob = await buildBiddingSummaryDocxBlob(root);
  if (!docBlob) return false;

  saveAs(docBlob, filename.endsWith('.docx') ? filename : `${filename}.docx`);
  return true;
};
